import { useState } from "react";
import { ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface Props {
  variant?: "default" | "outline" | "secondary" | "ghost";
  size?: "default" | "sm" | "lg" | "icon";
  className?: string;
}

/**
 * Calls founder-grant-admin which verifies founder status server-side
 * (FOUNDER_EMAIL or existing founders row) and grants the admin role
 * idempotently. Refreshes the session afterward so JWT/role claims update.
 */
export const GrantAdminButton = ({
  variant = "outline",
  size = "sm",
  className,
}: Props) => {
  const { toast } = useToast();
  const [busy, setBusy] = useState(false);

  const handleClick = async () => {
    setBusy(true);
    try {
      const { data, error } = await supabase.functions.invoke<{
        ok: boolean;
        granted: boolean;
        alreadyAdmin: boolean;
        error?: string;
      }>("founder-grant-admin");

      if (error || !data?.ok) {
        throw new Error(data?.error || error?.message || "grant_failed");
      }

      // Refresh JWT so any role-aware claims/policies pick it up.
      await supabase.auth.refreshSession().catch(() => {});

      if (data.alreadyAdmin) {
        toast({
          title: "Already an admin",
          description: "Your account already has the admin role.",
        });
      } else {
        toast({
          title: "Admin role granted",
          description: "Reloading so the new role takes effect…",
        });
        setTimeout(() => window.location.reload(), 400);
      }
    } catch (e) {
      toast({
        title: "Could not grant admin",
        description: e instanceof Error ? e.message : "Unknown error",
        variant: "destructive",
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Button
      type="button"
      variant={variant}
      size={size}
      className={className}
      onClick={handleClick}
      disabled={busy}
    >
      <ShieldCheck className={`h-4 w-4 mr-2 ${busy ? "animate-pulse" : ""}`} />
      {busy ? "Granting…" : "Grant admin role"}
    </Button>
  );
};

export default GrantAdminButton;
